import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_SECONDS = 30;

type Facing = 'environment' | 'user';

type Props = {
  value: string;
  disabled?: boolean;
  onChange: (videoDataUrl: string) => void;
};

const requestCamera = async (facing: Facing) => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: facing } },
      audio: true,
    });
  } catch {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing } },
        audio: true,
      });
    } catch {
      return navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    }
  }
};

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('video'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(blob);
  });

export const RoadDamageVideo = ({ value, disabled, onChange }: Props) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const keepClipRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const [facing, setFacing] = useState<Facing>('environment');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startPreview = useCallback(async () => {
    stopStream();
    setReady(false);
    setError('');
    try {
      const stream = await requestCamera(facing);
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      setReady(true);
    } catch {
      setError(t('roadDamages.cameraError'));
    }
  }, [facing, stopStream, t]);

  useEffect(() => {
    if (!open || value) return undefined;
    startPreview();
    return () => {
      clearTimer();
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
      stopStream();
    };
  }, [open, value, startPreview, stopStream]);

  const finishRecording = async () => {
    clearTimer();
    setRecording(false);
    const keep = keepClipRef.current;
    keepClipRef.current = false;
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'video/webm' });
    chunksRef.current = [];
    stopStream();
    if (!keep) return;
    try {
      onChange(await blobToDataUrl(blob));
      setOpen(false);
    } catch {
      setError(t('roadDamages.videoError'));
    }
  };

  const stopRecording = (keep: boolean) => {
    keepClipRef.current = keep;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    keepClipRef.current = true;
    const mime = ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'].find((type) =>
      typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)
    );
    let recorder: MediaRecorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      setError(t('roadDamages.videoError'));
      return;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      void finishRecording();
    };
    recorder.start(250);
    setRecording(true);
    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((current) => {
        if (current + 1 >= MAX_SECONDS) {
          keepClipRef.current = true;
          if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
          return MAX_SECONDS;
        }
        return current + 1;
      });
    }, 1000);
  };

  const closeRecorder = () => {
    clearTimer();
    stopRecording(false);
    stopStream();
    setOpen(false);
    setRecording(false);
    setSeconds(0);
  };

  if (value) {
    return (
      <div className="road-video-field">
        <video className="road-video-preview" src={value} controls playsInline />
        <button
          type="button"
          className="road-cancel"
          disabled={disabled}
          onClick={() => onChange('')}
        >
          {t('roadDamages.removeVideo')}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="road-video-field">
        <button
          type="button"
          className="road-download-btn"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {t('roadDamages.attachVideo')}
        </button>
        <p className="road-note">{t('roadDamages.videoMax')}</p>
      </div>
    );
  }

  return (
    <div className="road-video-field">
      <video ref={videoRef} autoPlay muted playsInline />
      {error && <p className="road-error">{error}</p>}
      {!error && !ready && <p className="road-note">{t('roadDamages.startingCamera')}</p>}
      {recording && (
        <p className="road-note">
          {t('roadDamages.videoSeconds', { seconds, max: MAX_SECONDS })}
        </p>
      )}
      <div className="road-camera-actions">
        <button type="button" className="road-cancel" disabled={recording} onClick={closeRecorder}>
          {t('cancel')}
        </button>
        <button
          type="button"
          className="road-cancel"
          disabled={recording || !ready}
          onClick={() => setFacing((current) => (current === 'environment' ? 'user' : 'environment'))}
        >
          {facing === 'environment'
            ? t('roadDamages.switchToFrontCamera')
            : t('roadDamages.switchToRearCamera')}
        </button>
        {recording ? (
          <button type="button" className="attach-damage-btn" onClick={() => stopRecording(true)}>
            {t('roadDamages.stopVideo')}
          </button>
        ) : (
          <button type="button" className="attach-damage-btn" disabled={!ready} onClick={startRecording}>
            {t('roadDamages.recordVideo')}
          </button>
        )}
      </div>
    </div>
  );
};
